/**
 * MFA Controller
 * Business Logic Layer - All MFA business logic and validation
 * Location: components/MFA/MFAController.ts
 */

import { verifyMFACodeAPI, resendMFACodeAPI } from "./MFAService";

/**
 * MFA Configuration Constants
 */
export const MFA_CONFIG = {
  OTP_LENGTH: 6,
  RESEND_TIMER_SECONDS: 60,
  AUTO_VERIFY_ON_COMPLETE: true,
  REDIRECT_DELAY_MS: 1000,
};

/**
 * Validate OTP format
 */
export const validateOTP = (otp: string[]): { isValid: boolean; message?: string } => {
  if (otp.length !== MFA_CONFIG.OTP_LENGTH) {
    return {
      isValid: false,
      message: `OTP must be ${MFA_CONFIG.OTP_LENGTH} digits`,
    };
  }

  const otpString = otp.join("");
  if (otpString.length !== MFA_CONFIG.OTP_LENGTH) {
    return {
      isValid: false,
      message: "Please enter all digits",
    };
  }

  if (!/^\d+$/.test(otpString)) {
    return {
      isValid: false,
      message: "OTP must contain only numbers",
    };
  }

  return { isValid: true };
};

/**
 * Determine MFA delivery method
 */
export const getMFADeliveryMethod = (showEmail: boolean, showPhone: boolean): string => {
  if (showEmail && showPhone) {
    return "both";
  } else if (showPhone) {
    return "sms";
  } else {
    return "email";
  }
};

/**
 * Get delivery information for UI display
 */
export const getDeliveryDisplayInfo = (
  showEmail: boolean,
  showPhone: boolean,
  mobileNumber?: string,
  userEmail?: string
): { text: string; detail: string; iconType: "email" | "phone" | "both" } => {
  if (showEmail && showPhone) {
    return {
      iconType: "both",
      text: "We've sent a 6-digit code to your email and mobile",
      detail: mobileNumber ? `Mobile: •••• ${mobileNumber.slice(-4)}` : "",
    };
  } else if (showPhone) {
    return {
      iconType: "phone",
      text: "We've sent a 6-digit code to your mobile number",
      detail: mobileNumber ? `•••• ${mobileNumber.slice(-4)}` : "",
    };
  } else {
    return {
      iconType: "email",
      text: "We've sent a 6-digit code to your email",
      detail: userEmail || "",
    };
  }
};

/**
 * Handle OTP verification
 */
export const handleOTPVerification = async (
  otp: string[]
): Promise<{ success: boolean; message?: string }> => {
  const validation = validateOTP(otp);
  if (!validation.isValid) {
    return {
      success: false,
      message: validation.message,
    };
  }

  const otpString = otp.join("");
  try {
    const result = await verifyMFACodeAPI(otpString);
    return result;
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : "Verification failed",
    };
  }
};

/**
 * Handle OTP resend
 */
export const handleOTPResend = async (
  userId: number,
  showEmail: boolean,
  showPhone: boolean
): Promise<{ success: boolean; message?: string }> => {
  const method = getMFADeliveryMethod(showEmail, showPhone);

  try {
    const success = await resendMFACodeAPI(userId, method);
    return {
      success,
      message: success ? "OTP sent successfully" : "Failed to send OTP",
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : "Failed to send OTP",
    };
  }
};

/**
 * Handle OTP input change
 */
export const handleOTPInputChange = (
  value: string,
  index: number,
  currentOtp: string[]
): { newOtp: string[]; shouldFocusNext: boolean; shouldFocusPrev: boolean } => {
  if (value && !/^\d$/.test(value)) {
    return {
      newOtp: currentOtp,
      shouldFocusNext: false,
      shouldFocusPrev: false,
    };
  }

  const newOtp = [...currentOtp];
  newOtp[index] = value;

  return {
    newOtp,
    shouldFocusNext: value !== "" && index < MFA_CONFIG.OTP_LENGTH - 1,
    shouldFocusPrev: false,
  };
};

/**
 * Handle backspace key
 */
export const handleOTPBackspace = (
  index: number,
  currentValue: string,
  currentOtp: string[]
): { newOtp: string[]; shouldFocusPrev: boolean } => {
  const newOtp = [...currentOtp];

  if (currentValue === "" && index > 0) {
    newOtp[index - 1] = "";
    return {
      newOtp,
      shouldFocusPrev: true,
    };
  } else {
    newOtp[index] = "";
    return {
      newOtp,
      shouldFocusPrev: false,
    };
  }
};

/**
 * Handle paste event
 */
export const handleOTPPaste = (pastedText: string): string[] | null => {
  const cleaned = pastedText.replace(/\D/g, "");

  if (cleaned.length === MFA_CONFIG.OTP_LENGTH) {
    return cleaned.split("");
  }

  return null;
};

/**
 * Check if OTP is complete
 */
export const isOTPComplete = (otp: string[]): boolean => {
  return otp.every((digit) => digit !== "") && otp.length === MFA_CONFIG.OTP_LENGTH;
};

/**
 * Reset OTP
 */
export const resetOTP = (): string[] => {
  return Array(MFA_CONFIG.OTP_LENGTH).fill("");
};

/**
 * Get MFA preferences from session
 */
export const getMFAPreferencesFromSession = (): {
  showEmail: boolean;
  showPhone: boolean;
  mobileNumber: string;
} => {
  return {
    showEmail: sessionStorage.getItem("showEmail") === "true",
    showPhone: sessionStorage.getItem("showPhone") === "true",
    mobileNumber: sessionStorage.getItem("mobileNumber") || "",
  };
};

/**
 * Clear MFA session data
 */
export const clearMFASession = (): void => {
  sessionStorage.removeItem("showEmail");
  sessionStorage.removeItem("showPhone");
  sessionStorage.removeItem("mobileNumber");
  sessionStorage.removeItem("mfaToken");
  sessionStorage.removeItem("mfaUserId");
  sessionStorage.removeItem("mfaFirstName");
};
